import React, { useState } from 'react';
import {
  MessageSquare, Mail, Bot, Filter, Users, FileDown, ChevronRight, ChevronLeft,
} from 'lucide-react';

export default function BulkActionsMenu({
  onClose,
  onAction,
  onAssignTextBot,
  onDispositionToBot,
  onAddToPipeline,
  onTransferToLeadAssist,
  aiBots = [],
  onBulkSms,
  onBulkEmail,
  onBulkExport,
  agents = [],
  dispositions = [],
  vendors = [],
  drips = [],
  campaigns = [],
}) {
  const [submenu, setSubmenu] = useState(null);

  const root = [
    { key: '__bulkSms', label: 'Send bulk SMS…', icon: 'sms', highlight: true },
    { key: '__bulkEmail', label: 'Send bulk email…', icon: 'email', highlight: true },
    { key: '__dispositionToBot', label: 'Disposition to AI Bot…', icon: 'bot', highlight: true },
    { key: 'enrollDrip', label: 'Add to drip campaign', sub: 'drips' },
    { key: 'enrollCampaign', label: 'Add to campaign', sub: 'campaigns' },
    { key: '__addToPipeline', label: 'Add to sales pipeline…', icon: 'pipeline', highlight: true },
    { key: '__bulkExport', label: 'Export selected to CSV', icon: 'export' },
    { divider: true },
    { key: '__transferToLeadAssist', label: 'Transfer to Lead Assist…', icon: 'leadAssist', highlight: true },
    { key: 'transfer', label: 'Transfer leads', sub: 'agents' },
    { key: 'addDisposition', label: 'Add disposition tags', sub: 'dispositions' },
    { key: 'removeDisposition', label: 'Remove disposition tags', sub: 'dispositions-remove' },
    { key: 'updateVendor', label: 'Update lead vendor', sub: 'vendors' },
    { key: 'assignTextBot', label: 'Assign to AI Text Bot', sub: 'textBots' },
    { divider: true },
    { key: 'markSold', label: 'Mark leads as sold' },
    { key: 'markUnsold', label: 'Mark leads as unsold' },
    { key: 'enrollBirthdayTexts', label: 'Enroll in birthday texts' },
    { key: 'removeBirthdayTexts', label: 'Remove from birthday texts' },
    { divider: true },
    { key: 'skipDrips', label: 'Skip all drips' },
    { key: 'skipEmailDrips', label: 'Skip email drips' },
    { key: 'skipSmsDrips', label: 'Skip SMS drips' },
    { key: 'unskipDrips', label: 'Unskip all drips' },
    { key: 'unskipEmailDrips', label: 'Unskip email drips' },
    { key: 'unskipSmsDrips', label: 'Unskip SMS drips' },
    { divider: true },
    { key: 'block', label: 'Block', danger: true },
    { key: 'unblock', label: 'Unblock' },
    { key: 'delete', label: 'Delete leads', danger: true },
  ];

  return (
    <div className="absolute right-0 mt-2 w-72 bg-[#0a0a1f] border border-white/15 ring-1 ring-black/40 rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden z-50">
      <div className="max-h-[480px] overflow-y-auto py-1">
        {!submenu && root.map((it, idx) => {
          if (it.divider) return <div key={`d${idx}`} className="h-px bg-white/5 my-1" />;
          return (
            <button
              key={it.key}
              onClick={() => {
                if (it.key === '__bulkSms') return onBulkSms?.();
                if (it.key === '__bulkEmail') return onBulkEmail?.();
                if (it.key === '__dispositionToBot') return onDispositionToBot?.();
                if (it.key === '__addToPipeline') return onAddToPipeline?.();
                if (it.key === '__transferToLeadAssist') return onTransferToLeadAssist?.();
                if (it.key === '__bulkExport') return onBulkExport?.();
                if (it.sub) setSubmenu(it);
                else {
                  if (it.danger && !window.confirm(`Confirm: ${it.label}?`)) return;
                  onAction(it.key, {});
                }
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center justify-between gap-2 ${
                it.danger ? 'text-red-300 hover:text-red-200' : ''
              } ${it.highlight ? 'text-civix-purple font-medium' : ''}`}
            >
              <span className="flex items-center gap-2">
                {it.icon === 'sms' && <MessageSquare className="w-3.5 h-3.5" />}
                {it.icon === 'email' && <Mail className="w-3.5 h-3.5" />}
                {it.icon === 'bot' && <Bot className="w-3.5 h-3.5" />}
                {it.icon === 'pipeline' && <Filter className="w-3.5 h-3.5" />}
                {it.icon === 'leadAssist' && <Users className="w-3.5 h-3.5" />}
                {it.icon === 'export' && <FileDown className="w-3.5 h-3.5" />}
                {it.label}
              </span>
              {it.sub && <ChevronRight className="w-3.5 h-3.5 text-white/40" />}
            </button>
          );
        })}

        {submenu?.sub === 'agents' && (
          <SubmenuList title="Transfer to agent" onBack={() => setSubmenu(null)}>
            {agents.map((a) => (
              <button
                key={a._id}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-white/5"
                onClick={() => onAction('transfer', { assignedAgent: a._id })}
              >{a.name || a.email}</button>
            ))}
          </SubmenuList>
        )}

        {submenu?.sub === 'vendors' && (
          <SubmenuList title="Update vendor" onBack={() => setSubmenu(null)}>
            {vendors.length === 0 && (
              <div className="px-3 py-2 text-xs text-white/40">No vendors yet — add one in Lead Vendors.</div>
            )}
            {vendors.map((v) => (
              <button
                key={v._id}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-white/5"
                onClick={() => onAction('updateVendor', { vendor: v._id })}
              >{v.name}</button>
            ))}
          </SubmenuList>
        )}

        {submenu?.sub === 'textBots' && (
          <SubmenuList title="Assign to AI Text Bot" onBack={() => setSubmenu(null)}>
            {aiBots.length === 0 && (
              <div className="px-3 py-2 text-xs text-white/40">
                No text bots yet — create one in AI Text Agent.
              </div>
            )}
            {aiBots.map((b) => (
              <button
                key={b._id}
                data-testid={`assign-text-bot-${b._id}`}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-white/5"
                onClick={() => onAssignTextBot?.(b._id)}
              >{b.name}</button>
            ))}
            <div className="h-px bg-white/5 my-1" />
            <button
              data-testid="assign-text-bot-none"
              className="w-full text-left px-3 py-1.5 text-sm text-white/70 hover:bg-white/5"
              onClick={() => onAssignTextBot?.(null)}
            >None (unassign)</button>
          </SubmenuList>
        )}

        {submenu?.sub === 'dispositions' && (
          <SubmenuList title="Add disposition" onBack={() => setSubmenu(null)}>
            {dispositions.map((d) => (
              <button
                key={d._id}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-white/5 flex items-center gap-2"
                onClick={() => onAction('addDisposition', { dispositionId: d._id })}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                {d.label}
              </button>
            ))}
          </SubmenuList>
        )}
        {submenu?.sub === 'drips' && (
          <SubmenuList title="Add to drip" onBack={() => setSubmenu(null)}>
            {drips.length === 0 && (
              <div className="px-3 py-2 text-xs text-white/40">
                No drips yet — create one in Marketing → Drip Builder.
              </div>
            )}
            {drips.map((d) => (
              <button
                key={d._id}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-white/5"
                onClick={() => onAction('enrollDrip', { dripGroupId: d.dripGroupId || d._id })}
              >{d.name}</button>
            ))}
          </SubmenuList>
        )}

        {submenu?.sub === 'campaigns' && (
          <SubmenuList title="Add to campaign" onBack={() => setSubmenu(null)}>
            {campaigns.length === 0 && (
              <div className="px-3 py-2 text-xs text-white/40">No campaigns yet — create one in Campaigns.</div>
            )}
            {campaigns.map((c) => (
              <button
                key={c._id}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-white/5"
                onClick={() => onAction('enrollCampaign', { campaignId: c._id })}
              >{c.name}</button>
            ))}
          </SubmenuList>
        )}

        {submenu?.sub === 'dispositions-remove' && (
          <SubmenuList title="Remove disposition" onBack={() => setSubmenu(null)}>
            {dispositions.map((d) => (
              <button
                key={d._id}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-white/5 flex items-center gap-2"
                onClick={() => onAction('removeDisposition', { slug: d.slug })}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                {d.label}
              </button>
            ))}
          </SubmenuList>
        )}
      </div>
    </div>
  );
}

function SubmenuList({ title, onBack, children }) {
  return (
    <div>
      <button
        onClick={onBack}
        className="w-full px-3 py-2 text-[11px] uppercase tracking-wider text-white/40 hover:bg-white/5 flex items-center gap-1 border-b border-white/10"
      >
        <ChevronLeft className="w-3 h-3" /> {title}
      </button>
      <div className="max-h-[320px] overflow-y-auto">{children}</div>
    </div>
  );
}
